import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAppDispatch } from '@/state/store';
import { RootState } from '@/state/store';
import { userSelectedComponent } from '@/state/slices/userSelectedComponentSlice';
import { getComponentList } from '@/components/smartai/api/getComponentList';
import {
  getRepositoryWebhookConfig,
  getWebhookConfigErrorMessage,
  normalizeWebhookConfig,
  saveRepositoryWebhookConfig,
} from '@/components/smartai/api/webhookConfigApi';

interface WebhookSettingsPanelProps {
  /** True while the settings sheet is open. */
  active: boolean;
  /** Active workspace repository id from the current route. */
  repoId?: string;
}

const webhookRowClass =
  'flex items-center justify-between gap-3 border-0 border-b border-solid border-[var(--inv-border-soft)] px-3 py-2.5';

const webhookBodyClass = 'flex flex-col gap-3 px-3 py-3';

const fieldLabelClass =
  'mb-1.5 block text-[0.8125rem] font-medium leading-none tracking-wide text-[var(--inv-text-secondary)]';

const secretInputClassName =
  '[&::-ms-clear]:hidden [&::-ms-reveal]:hidden [&::-webkit-credentials-auto-fill-button]:hidden [&::-webkit-contacts-auto-fill-button]:hidden';

const WebhookSettingsPanel: React.FC<WebhookSettingsPanelProps> = ({
  active,
  repoId: repoIdProp,
}) => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const selectedComponent = useSelector(
    (state: RootState) => state.userSelectedComponentSlice,
  );
  const repoId = repoIdProp?.trim() || undefined;
  const fallbackWebhookEnabled = Boolean(selectedComponent.isWebhookEnabled);

  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [initialWebhookEnabled, setInitialWebhookEnabled] = useState(false);
  const [hasSecret, setHasSecret] = useState(false);
  const [secret, setSecret] = useState('');
  const [isEditingSecret, setIsEditingSecret] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!active || !repoId) {
      return;
    }
    let cancelled = false;

    const loadConfig = async () => {
      setLoading(true);
      setError(null);
      setSaveSuccessMessage(null);
      setSecret('');
      setIsEditingSecret(false);
      try {
        const config = normalizeWebhookConfig(await getRepositoryWebhookConfig(repoId));
        if (cancelled) return;
        const enabled = Boolean(config.isWebhookEnabled) || fallbackWebhookEnabled;
        setWebhookEnabled(enabled);
        setInitialWebhookEnabled(enabled);
        setHasSecret(Boolean(config.hasSecret) || enabled);
      } catch (loadError) {
        if (!cancelled) {
          if (fallbackWebhookEnabled) {
            setWebhookEnabled(true);
            setInitialWebhookEnabled(true);
            setHasSecret(true);
            setError(null);
          } else {
            setError(
              getWebhookConfigErrorMessage(
                loadError,
                'Failed to load webhook configuration.',
              ),
            );
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadConfig();

    return () => {
      cancelled = true;
    };
  }, [active, repoId, fallbackWebhookEnabled]);

  const isFirstTimeSetup = webhookEnabled && !initialWebhookEnabled;
  const isConfiguredView =
    webhookEnabled && !isEditingSecret && !isFirstTimeSetup;
  const showSetupOrEditForm =
    webhookEnabled && (isFirstTimeSetup || isEditingSecret);

  const handleEnableChange = (checked: boolean) => {
    setWebhookEnabled(checked);
    if (!checked) {
      setSecret('');
      setIsEditingSecret(false);
    }
    setError(null);
    setSaveSuccessMessage(null);
  };

  const handleStartEditSecret = () => {
    setIsEditingSecret(true);
    setSecret('');
    setError(null);
    setSaveSuccessMessage(null);
  };

  const handleCancel = () => {
    setIsEditingSecret(false);
    setSecret('');
    setWebhookEnabled(initialWebhookEnabled);
    setError(null);
    setSaveSuccessMessage(null);
  };

  const persistConfig = async () => {
    if (!repoId) {
      return;
    }
    const trimmedSecret = secret.trim();
    const isUpdatingSecret = isEditingSecret && trimmedSecret.length > 0;
    const needsNewSecret = isFirstTimeSetup;

    if (needsNewSecret && !trimmedSecret) {
      setError('Secret is required when webhook is enabled.');
      return;
    }

    if (isEditingSecret && !trimmedSecret) {
      setError('Enter a new webhook secret.');
      return;
    }

    setSaving(true);
    setError(null);
    setSaveSuccessMessage(null);
    try {
      const saved = normalizeWebhookConfig(
        await saveRepositoryWebhookConfig(repoId, {
          isWebhookEnabled: webhookEnabled,
          secret:
            webhookEnabled && trimmedSecret.length > 0 ? trimmedSecret : null,
          updateSecret: isEditingSecret && trimmedSecret.length > 0,
        }),
      );
      setWebhookEnabled(Boolean(saved.isWebhookEnabled));
      setInitialWebhookEnabled(Boolean(saved.isWebhookEnabled));
      setHasSecret(Boolean(saved.hasSecret));
      setSecret('');
      setIsEditingSecret(false);
      await queryClient.invalidateQueries({ queryKey: ['component-list'] });
      await queryClient.invalidateQueries({ queryKey: ['webhook-config', repoId] });
      try {
        const componentList = await getComponentList();
        const updatedRepo = componentList.data?.find(item => item.dbRepoId === repoId);
        if (updatedRepo) {
          dispatch(userSelectedComponent(updatedRepo));
        }
      } catch {
        // Non-fatal: poller UI will refresh on next component-list fetch.
      }
      if (isUpdatingSecret) {
        setSaveSuccessMessage('Webhook secret updated.');
      } else {
        setSaveSuccessMessage('Webhook configuration saved.');
      }
    } catch (saveError) {
      setError(
        getWebhookConfigErrorMessage(saveError, 'Failed to save webhook configuration.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const showActions =
    !loading &&
    (webhookEnabled !== initialWebhookEnabled ||
      isEditingSecret ||
      isFirstTimeSetup ||
      (webhookEnabled && !hasSecret));

  return (
    <div className="flex flex-col">
      {loading ? (
        <p className="m-0 px-3 py-2.5 text-[0.8125rem] text-[var(--inv-text-secondary)]">
          Loading...
        </p>
      ) : (
        <>
          <div className={webhookRowClass}>
            <span className="text-[0.8125rem] text-[var(--inv-text)]">
              Enable webhook
            </span>
            <Switch
              id="settings-enable-webhook"
              checked={webhookEnabled}
              disabled={!repoId || saving}
              onChange={handleEnableChange}
            />
          </div>

          {isConfiguredView ? (
            <div className={cn(webhookRowClass, 'last:border-b-0')}>
              <p className="m-0 text-[0.8125rem] text-[var(--inv-text-secondary)]">
                Secret is configured
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 shrink-0 p-0 text-[var(--inv-text-muted)] hover:bg-[var(--inv-surface-muted)] hover:text-[var(--inv-text)]"
                aria-label="Edit webhook secret"
                disabled={saving}
                onClick={handleStartEditSecret}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : null}

          {showSetupOrEditForm || error || saveSuccessMessage || showActions ? (
            <div className={webhookBodyClass}>
              {showSetupOrEditForm ? (
                <div>
                  <label htmlFor="settings-webhook-secret" className={fieldLabelClass}>
                    Secret
                  </label>
                  <Input
                    id="settings-webhook-secret"
                    type="password"
                    value={secret}
                    disabled={!repoId || saving}
                    onChange={event => setSecret(event.target.value)}
                    placeholder="Enter webhook secret"
                    autoComplete="new-password"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    className={secretInputClassName}
                  />
                </div>
              ) : null}

              {error ? (
                <p className="m-0 text-[0.8125rem] text-[#b42318]">{error}</p>
              ) : null}
              {saveSuccessMessage ? (
                <p className="m-0 text-[0.8125rem] text-[var(--inv-accent)]">
                  {saveSuccessMessage}
                </p>
              ) : null}

              {showActions ? (
                <div className="flex justify-end gap-2 pt-1">
                  {(isEditingSecret || isFirstTimeSetup) && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={saving}
                      onClick={handleCancel}>
                      Cancel
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    disabled={!repoId || loading || saving}
                    onClick={() => void persistConfig()}>
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default WebhookSettingsPanel;
